import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal } from "../components/Reveal";
import { EnquiryForm } from "../components/EnquiryForm";
import { Slider } from "../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { IRAS_LINKS } from "../lib/site";
import { computeBSD, ABSD_PROFILES, computeAffordability } from "../lib/affordability.mjs";

const CPF_PLANNER_URL = "https://www.cpf.gov.sg/member/tools-and-services/planners/home-purchase";

const fmt = (n) => "$" + Math.round(Number.isFinite(n) ? n : 0).toLocaleString("en-SG");
const num = (v) => Number(String(v).replace(/[^0-9.]/g, "")) || 0;

const TABS = [
  { id: "mortgage", label: "Mortgage Repayment" },
  { id: "stamp-duty", label: "Stamp Duty" },
  { id: "affordability", label: "Affordability" },
  { id: "valuation", label: "Home Valuation" },
];

const Field = ({ label, children, htmlFor, hint }) => (
  <div>
    <label htmlFor={htmlFor} className="eyebrow mb-2 block text-ivory/60">{label}</label>
    {children}
    {hint && <p className="mt-1.5 text-[0.68rem] text-ivory/40">{hint}</p>}
  </div>
);

const NumberInput = ({ id, value, onChange, testId, prefix }) => (
  <div className="flex items-center border border-ivory/20 bg-white/5 focus-within:border-ivory">
    {prefix && <span className="pl-4 text-sm text-ivory/50">{prefix}</span>}
    <input
      id={id}
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      inputMode="decimal"
      className="h-12 w-full bg-transparent px-4 text-sm text-ivory focus:outline-none"
    />
  </div>
);

const DarkSelect = ({ id, testId, value, onChange, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger id={id} data-testid={testId} className="h-12 rounded-none border-ivory/20 bg-white/5 text-sm text-ivory">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const ResultRow = ({ label, value, strong }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-ivory/10 py-3.5">
    <span className="text-[0.7rem] uppercase tracking-[0.2em] text-ivory/55">{label}</span>
    <span className={`font-serif ${strong ? "text-3xl" : "text-xl"} text-ivory`}>{value}</span>
  </div>
);

const ResultCard = ({ eyebrow, children }) => (
  <div className="border border-ivory/10 bg-[#242424] p-8 sm:p-10">
    <p className="eyebrow text-ivory/60">{eyebrow}</p>
    <div className="mt-6">{children}</div>
  </div>
);

const Disclaimer = ({ children }) => (
  <p className="mt-6 border-l-2 border-ivory/40 pl-4 text-[0.72rem] leading-relaxed text-ivory/55" data-testid="tool-disclaimer">
    {children}
  </p>
);

function MortgageCalc() {
  const [amount, setAmount] = useState(2000000);
  const [rate, setRate] = useState(4.5);
  const [years, setYears] = useState(25);

  const { monthly, totalInterest, totalPayable } = useMemo(() => {
    const r = rate / 100 / 12;
    const n = years * 12;
    if (amount <= 0 || n <= 0) return { monthly: 0, totalInterest: 0, totalPayable: 0 };
    if (r <= 0) return { monthly: amount / n, totalInterest: 0, totalPayable: amount };
    const m = (amount * r) / (1 - Math.pow(1 + r, -n));
    return { monthly: m, totalInterest: m * n - amount, totalPayable: m * n };
  }, [amount, rate, years]);

  return (
    <div className="grid gap-12 lg:grid-cols-2" data-testid="mortgage-calculator">
      <div className="space-y-8">
        <Field label="Loan amount" htmlFor="mc-amount">
          <NumberInput id="mc-amount" testId="mortgage-amount-input" prefix="$" value={amount.toLocaleString()} onChange={(v) => setAmount(Math.min(num(v), 50000000))} />
          <Slider data-testid="mortgage-amount-slider" className="mt-4" min={500000} max={15000000} step={50000} value={[Math.min(amount, 15000000)]} onValueChange={([v]) => setAmount(v)} />
        </Field>
        <Field label="Interest rate (% p.a.)" htmlFor="mc-rate">
          <NumberInput id="mc-rate" testId="mortgage-rate-input" value={rate} onChange={(v) => setRate(Math.min(Math.max(num(v), 0), 10))} />
          <Slider data-testid="mortgage-rate-slider" className="mt-4" min={0.5} max={6} step={0.05} value={[rate]} onValueChange={([v]) => setRate(v)} />
        </Field>
        <Field label="Loan tenure (years)" htmlFor="mc-years">
          <NumberInput id="mc-years" testId="mortgage-tenure-input" value={years} onChange={(v) => setYears(Math.min(Math.max(num(v), 1), 35))} />
          <Slider data-testid="mortgage-tenure-slider" className="mt-4" min={5} max={35} step={1} value={[years]} onValueChange={([v]) => setYears(v)} />
        </Field>
      </div>
      <div>
        <ResultCard eyebrow="Your estimate">
          <ResultRow label="Monthly repayment" value={fmt(monthly)} strong />
          <ResultRow label="Total interest" value={fmt(totalInterest)} />
          <ResultRow label="Total payable" value={fmt(totalPayable)} />
          <p className="mt-6 text-xs text-ivory/50" data-testid="mortgage-result-note">
            {years}-year loan of {fmt(amount)} at {rate}% p.a. · Standard amortisation, rate held constant.
          </p>
          <Disclaimer>
            Demonstration estimate only, computed in your browser. Actual packages, lock-ins, fees and bank assessments
            differ. This is not financial advice — Andy can connect you with trusted mortgage bankers for live rates.
          </Disclaimer>
        </ResultCard>
      </div>
    </div>
  );
}

function StampDutyCalc() {
  const [price, setPrice] = useState(5000000);
  const [profileId, setProfileId] = useState("sc1");

  const bsd = useMemo(() => computeBSD(price), [price]);
  const profile = ABSD_PROFILES.find((p) => p.id === profileId);
  const absd = price * profile.rate;
  const total = bsd + absd;

  return (
    <div className="grid gap-12 lg:grid-cols-2" data-testid="stamp-duty-calculator">
      <div className="space-y-8">
        <Field label="Purchase price" htmlFor="sd-price">
          <NumberInput id="sd-price" testId="stamp-duty-price-input" prefix="$" value={price.toLocaleString()} onChange={(v) => setPrice(Math.min(num(v), 200000000))} />
          <Slider data-testid="stamp-duty-price-slider" className="mt-4" min={1000000} max={30000000} step={100000} value={[Math.min(price, 30000000)]} onValueChange={([v]) => setPrice(v)} />
        </Field>
        <Field label="Buyer profile" htmlFor="sd-profile">
          <DarkSelect id="sd-profile" testId="stamp-duty-profile-select" value={profileId} onChange={setProfileId}
            options={ABSD_PROFILES.map((p) => ({ value: p.id, label: p.label }))} />
        </Field>
        <div className="border border-ivory/10 bg-white/5 p-6 text-xs leading-relaxed text-ivory/60">
          <p className="font-semibold uppercase tracking-[0.18em] text-ivory/70">Rules applied</p>
          <p className="mt-2">Buyer's Stamp Duty (BSD) residential tiers effective <strong className="text-ivory">15 Feb 2023</strong>; Additional Buyer's Stamp Duty (ABSD) residential rates effective <strong className="text-ivory">27 Apr 2023</strong>.</p>
          <div className="mt-3 flex flex-wrap gap-4">
            <a href={IRAS_LINKS.bsd} target="_blank" rel="noopener noreferrer" data-testid="iras-bsd-link" className="inline-flex items-center gap-1 font-semibold text-ivory underline underline-offset-4">
              IRAS — BSD <ExternalLink className="h-3 w-3" />
            </a>
            <a href={IRAS_LINKS.absd} target="_blank" rel="noopener noreferrer" data-testid="iras-absd-link" className="inline-flex items-center gap-1 font-semibold text-ivory underline underline-offset-4">
              IRAS — ABSD <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>
      <div>
        <ResultCard eyebrow="Duty estimate">
          <ResultRow label="Buyer's Stamp Duty" value={fmt(bsd)} />
          <ResultRow label={`ABSD (${(profile.rate * 100).toFixed(0)}%)`} value={fmt(absd)} />
          <ResultRow label="Total duties" value={fmt(total)} strong />
          <Disclaimer>
            Demonstration only. Stamp duty rules and rates change — always verify the current schedules on
            iras.gov.sg or with your conveyancing lawyer before relying on any figure. Joint-buyer, remission and
            exemption cases are not modelled. This tool is not tax, legal or financial advice.
          </Disclaimer>
        </ResultCard>
      </div>
    </div>
  );
}

const BINDING_LABELS = {
  funds: "Your available cash / CPF funds",
  tdsr: "The 55% TDSR debt-servicing limit",
  ltv: "The LTV (loan-to-value) limit",
};

function AffordabilityPlanner() {
  const [income, setIncome] = useState(25000);
  const [coIncome, setCoIncome] = useState(0);
  const [age, setAge] = useState(38);
  const [tenure, setTenure] = useState(25);
  const [debts, setDebts] = useState(1500);
  const [outstandingLoans, setOutstandingLoans] = useState("0");
  const [housingPayments, setHousingPayments] = useState(0);
  const [cash, setCash] = useState(400000);
  const [cpf, setCpf] = useState(300000);
  const [profileId, setProfileId] = useState("sc1");
  const [actualRate, setActualRate] = useState(4.5);
  const [otherCosts, setOtherCosts] = useState(3500);

  const r = useMemo(() => computeAffordability({
    income, coIncome, age, tenure, debts,
    outstandingLoans: Number(outstandingLoans),
    housingPayments: Number(outstandingLoans) > 0 ? housingPayments : 0,
    cash, cpf, actualRate, profileId, otherCosts,
  }), [income, coIncome, age, tenure, debts, outstandingLoans, housingPayments, cash, cpf, actualRate, profileId, otherCosts]);

  return (
    <div className="grid gap-12 lg:grid-cols-2" data-testid="affordability-planner">
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-5">
          <Field label="Your gross monthly income" htmlFor="af-income">
            <NumberInput id="af-income" testId="affordability-income-input" prefix="$" value={income.toLocaleString()} onChange={(v) => setIncome(num(v))} />
          </Field>
          <Field label="Co-borrower income (if any)" htmlFor="af-coincome">
            <NumberInput id="af-coincome" testId="affordability-coincome-input" prefix="$" value={coIncome ? coIncome.toLocaleString() : ""} onChange={(v) => setCoIncome(num(v))} />
          </Field>
          <Field label="Your age" htmlFor="af-age" hint="Older borrower if buying jointly">
            <NumberInput id="af-age" testId="affordability-age-input" value={age} onChange={(v) => setAge(Math.min(Math.max(num(v), 21), 70))} />
          </Field>
          <Field label="Loan tenure (years)" htmlFor="af-tenure" hint="Max 35 yrs; past age 65 lowers LTV">
            <NumberInput id="af-tenure" testId="affordability-tenure-input" value={tenure} onChange={(v) => setTenure(Math.min(Math.max(num(v), 5), 35))} />
          </Field>
        </div>
        <Field label="Existing monthly debts (car, personal loans…)" htmlFor="af-debts">
          <NumberInput id="af-debts" testId="affordability-debts-input" prefix="$" value={debts ? debts.toLocaleString() : ""} onChange={(v) => setDebts(num(v))} />
        </Field>
        <div className="grid grid-cols-2 gap-5">
          <Field label="Outstanding housing loans" htmlFor="af-loans">
            <DarkSelect id="af-loans" testId="affordability-loans-select" value={outstandingLoans} onChange={setOutstandingLoans}
              options={[{ value: "0", label: "None" }, { value: "1", label: "One" }, { value: "2", label: "Two or more" }]} />
          </Field>
          {Number(outstandingLoans) > 0 && (
            <Field label="Their monthly repayments" htmlFor="af-housing">
              <NumberInput id="af-housing" testId="affordability-housing-input" prefix="$" value={housingPayments ? housingPayments.toLocaleString() : ""} onChange={(v) => setHousingPayments(num(v))} />
            </Field>
          )}
        </div>
        <div className="grid grid-cols-2 gap-5">
          <Field label="Cash available" htmlFor="af-cash">
            <NumberInput id="af-cash" testId="affordability-cash-input" prefix="$" value={cash.toLocaleString()} onChange={(v) => setCash(num(v))} />
          </Field>
          <Field label="CPF OA you can use" htmlFor="af-cpf" hint="Subject to CPF Board limits">
            <NumberInput id="af-cpf" testId="affordability-cpf-input" prefix="$" value={cpf.toLocaleString()} onChange={(v) => setCpf(num(v))} />
          </Field>
        </div>
        <Field label="Buyer profile (for ABSD)" htmlFor="af-profile">
          <DarkSelect id="af-profile" testId="affordability-profile-select" value={profileId} onChange={setProfileId}
            options={ABSD_PROFILES.map((p) => ({ value: p.id, label: p.label }))} />
        </Field>
        <div className="grid grid-cols-2 gap-5">
          <Field label="Actual mortgage rate (% p.a.)" htmlFor="af-rate">
            <NumberInput id="af-rate" testId="affordability-rate-input" value={actualRate} onChange={(v) => setActualRate(Math.min(Math.max(num(v), 0), 10))} />
          </Field>
          <Field label="Legal & other upfront costs" htmlFor="af-costs">
            <NumberInput id="af-costs" testId="affordability-costs-input" prefix="$" value={otherCosts.toLocaleString()} onChange={(v) => setOtherCosts(num(v))} />
          </Field>
        </div>
      </div>

      <div>
        <ResultCard eyebrow="Your indicative budget">
          <ResultRow label="Affordable purchase price" value={fmt(r.maxPrice)} strong />
          <ResultRow label="Maximum housing loan" value={fmt(r.maxLoan)} />
          <ResultRow label={`Monthly instalment @ ${r.actualRate}%`} value={fmt(r.monthlyActual)} />
          <ResultRow label="LTV applied" value={`${(r.ltv * 100).toFixed(0)}%`} />
          <ResultRow label="Stress-test rate used" value={`${r.stressRate.toFixed(2)}%`} />
          <ResultRow label="Required downpayment" value={fmt(r.downpayment)} />
          <ResultRow label="Minimum cash component" value={fmt(r.minCash)} />
          <ResultRow label="Buyer's Stamp Duty" value={fmt(r.bsd)} />
          <ResultRow label={`ABSD (${(r.profile.rate * 100).toFixed(0)}%)`} value={fmt(r.absd)} />
          <ResultRow label="Total upfront (incl. costs)" value={fmt(r.upfrontTotal)} />
          <ResultRow label="What limits you" value={BINDING_LABELS[r.binding]} />

          {r.warnings.length > 0 && (
            <ul className="mt-5 space-y-2" data-testid="affordability-warnings">
              {r.warnings.map((w) => (
                <li key={w} className="border-l-2 border-amber-300/60 pl-3 text-xs leading-relaxed text-ivory/70">{w}</li>
              ))}
            </ul>
          )}

          <div className="mt-6 border-t border-ivory/10 pt-5" data-testid="affordability-assumptions">
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-ivory/55">Key assumptions</p>
            <ul className="mt-3 space-y-1.5">
              {r.assumptions.map((a) => (
                <li key={a} className="text-[0.72rem] leading-relaxed text-ivory/50">— {a}</li>
              ))}
            </ul>
          </div>

          <Disclaimer>
            Figures are indicative estimates only and do not constitute financial advice or a bank loan approval.
            Actual eligibility, CPF usage and financing terms are subject to the relevant authorities and lenders.{" "}
            Verify your position with the{" "}
            <a href={CPF_PLANNER_URL} target="_blank" rel="noopener noreferrer" data-testid="cpf-planner-link" className="font-semibold text-ivory underline underline-offset-4">
              CPF Home Purchase Planner <ExternalLink className="inline h-3 w-3" />
            </a>
            .
          </Disclaimer>
        </ResultCard>
      </div>
    </div>
  );
}

export default function Tools() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "mortgage";

  return (
    <main className="pt-[72px]" data-testid="tools-page">
      <section className="border-b border-ivory/10 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHeading
            eyebrow="Financial Tools"
            title={<>Run the numbers<br />before you fall in love.</>}
            description="Working demonstration calculators for the preview. Each carries honest assumptions, effective dates and links to official sources."
          />
        </div>
      </section>

      <section className="py-14 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Financial tools" data-testid="tools-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => setSearchParams({ tab: t.id }, { replace: true })}
                data-testid={`tools-tab-${t.id}`}
                className={`px-6 py-3 text-[0.72rem] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
                  tab === t.id ? "bg-brand text-ivory" : "border border-ivory/20 text-ivory/70 hover:border-ivory hover:text-ivory"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <Reveal className="mt-12" key={tab}>
            {tab === "mortgage" && <MortgageCalc />}
            {tab === "stamp-duty" && <StampDutyCalc />}
            {tab === "affordability" && <AffordabilityPlanner />}
            {tab === "valuation" && (
              <div className="grid gap-12 lg:grid-cols-2" data-testid="valuation-section">
                <div>
                  <h3 className="font-serif text-3xl text-ivory">A valuation worth signing for</h3>
                  <p className="mt-5 text-base leading-relaxed text-ivory/70">
                    An honest valuation of a landed home weighs land content, plot shape, orientation, zoning,
                    recent comparable transactions and condition — no automated widget can price that credibly,
                    and this preview will not pretend otherwise.
                  </p>
                  <p className="mt-4 text-base leading-relaxed text-ivory/70">
                    Tell Andy about your property and his team will prepare a considered, human review. For formal
                    bank valuations, a licensed valuer or an authorised data provider is engaged.
                  </p>
                  <Disclaimer>
                    This is a lead enquiry, not an automated valuation. Nothing submitted here produces a price
                    figure, and no estimate should be relied upon without professional review.
                  </Disclaimer>
                </div>
                <div className="border border-ivory/10 bg-[#242424] p-8 sm:p-10">
                  <EnquiryForm kind="valuation" compact />
                </div>
              </div>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
}
